import React from 'react';
import { Button as MuiButton } from '@mui/material';
import { COLOR, FOCUS_VISIBLE_SX, RADIUS, DURATION } from './tokens';

const VARIANT_SX = {
    primary: {
        bgcolor: COLOR.primary,
        color: COLOR.primaryContrast,
        border: '1px solid transparent',
        '&:hover': { bgcolor: COLOR.primaryHover },
    },
    secondary: {
        bgcolor: 'transparent',
        color: COLOR.text,
        border: `1px solid ${COLOR.borderStrong}`,
        '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', borderColor: COLOR.textSubtle },
    },
    ghost: {
        bgcolor: 'transparent',
        color: COLOR.textMuted,
        border: '1px solid transparent',
        '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', color: COLOR.text },
    },
    danger: {
        bgcolor: 'transparent',
        color: COLOR.danger,
        border: `1px solid ${COLOR.borderStrong}`,
        '&:hover': { bgcolor: 'rgba(239,71,67,0.1)', borderColor: COLOR.danger },
    },
};

const SIZE_SX = {
    sm: { px: 1.5, py: 0.5, fontSize: '0.8125rem', minHeight: 32 },
    md: { px: 2.5, py: 1, fontSize: '0.9375rem', minHeight: 40 },
    lg: { px: 4, py: 1.25, fontSize: '1.05rem', minHeight: 48 },
};

/**
 * Arcade button. One look per role: `primary` (green fill, the main action),
 * `secondary` (outlined), `ghost` (text) and `danger` (End / destructive).
 */
const Button = React.forwardRef(function ArcadeButton(
    { variant = 'primary', size = 'md', sx, children, ...rest },
    ref,
) {
    return (
        <MuiButton
            ref={ref}
            disableElevation
            data-variant={variant}
            sx={[
                {
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: RADIUS.md,
                    transition: `background-color ${DURATION}, border-color ${DURATION}, color ${DURATION}`,
                    '&.Mui-disabled': { opacity: 0.5, color: COLOR.textMuted },
                },
                VARIANT_SX[variant] || VARIANT_SX.primary,
                SIZE_SX[size] || SIZE_SX.md,
                FOCUS_VISIBLE_SX,
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
            {...rest}
        >
            {children}
        </MuiButton>
    );
});

export default Button;
